// =============================================================
// Settings Page
// Multi-tenant SaaS  Phase 7: Product Activation
// Profile, Organization, and Real Active Integrations
// =============================================================

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { googleApi, businessApi, GoogleStatusResponse } from '../lib/api';

import { IconGoogleBusiness, IconGoogleSearch, IconGemini } from '../components/icons';

export default function SettingsPage() {
  const { user, organization } = useAuth();

  const [activeBizId, setActiveBizId] = useState<number | null>(() => {
    const saved = localStorage.getItem('active_dashboard_business_id');
    return saved ? parseInt(saved, 10) : null;
  });

  const [googleStatus, setGoogleStatus] = useState<GoogleStatusResponse | null>(null);
  const [googleLoading, setGoogleLoading] = useState(true);
  const [googleError, setGoogleError] = useState<string | null>(null);

  const fetchGoogleStatus = useCallback(async () => {
    try {
      setGoogleLoading(true);
      setGoogleError(null);
      const res = await googleApi.getStatus();
      setGoogleStatus(res);
    } catch {
      setGoogleError('Unable to determine integration status.');
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoogleStatus();
  }, [fetchGoogleStatus]);

  useEffect(() => {
    let mounted = true;
    businessApi
      .list()
      .then((list) => {
        if (!mounted || list.length === 0) return;
        setActiveBizId((prev) => {
          if (prev && list.some((b) => b.id === prev)) return prev;
          const saved = localStorage.getItem('active_dashboard_business_id');
          const savedNum = saved ? parseInt(saved, 10) : null;
          const match = savedNum ? list.find((b) => b.id === savedNum) : null;
          const chosen = match ? match.id : list[0].id;
          localStorage.setItem('active_dashboard_business_id', String(chosen));
          return chosen;
        });
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [organization?.id]);

  const googleTargetUrl = activeBizId ? `/businesses/${activeBizId}/integrations` : '/businesses';
  const aiTargetUrl = user?.isSuperAdmin
    ? '/admin/ai-config'
    : (activeBizId ? `/businesses/${activeBizId}/intelligence` : '/businesses');

  const isGoogleConnected = Boolean(googleStatus?.connected);
  const googleAccountEmail = googleStatus?.connection?.googleAccountEmail;

  return (
    <DashboardLayout title="Settings" subtitle="Manage your account, organization, and integrations">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Settings</h1>
          <p>Manage your account, organization, and preferences</p>
        </div>
      </div>

      {/* Profile section */}
      <div className="settings-section">
        <div className="settings-section-header">
          <div className="settings-section-title">Profile</div>
          <div className="settings-section-desc">Your personal account information</div>
        </div>
        <div className="settings-section-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input
                type="text"
                className="form-input"
                value={user?.firstName ?? ''}
                disabled
                readOnly
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-input"
                value={user?.lastName ?? ''}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              className="form-input"
              value={user?.email ?? ''}
              disabled
              readOnly
            />
          </div>
          <div className="alert alert-warning" style={{ marginTop: 4 }}>
            <span></span>
            Profile editing will be available in a future update.
          </div>
        </div>
      </div>

      {/* Organization section */}
      <div className="settings-section">
        <div className="settings-section-header">
          <div className="settings-section-title">Organization</div>
          <div className="settings-section-desc">Your organization workspace details</div>
        </div>
        <div className="settings-section-body">
          <div className="form-group">
            <label className="form-label">Organization Name</label>
            <input
              type="text"
              className="form-input"
              value={organization?.name ?? ''}
              disabled
              readOnly
            />
          </div>
          <div className="form-group">
            <label className="form-label">Organization Slug</label>
            <input
              type="text"
              className="form-input"
              value={organization?.slug ?? ''}
              disabled
              readOnly
            />
            <span className="form-hint">
              Used in internal identifiers. Cannot be changed.
            </span>
          </div>
        </div>
      </div>

      {/* Integrations section */}
      <div className="settings-section">
        <div className="settings-section-header">
          <div className="settings-section-title">Integrations</div>
          <div className="settings-section-desc">
            Connected external services and verified API channels
          </div>
        </div>
        <div className="settings-section-body">
          {/* Error notice if status check failed */}
          {googleError && (
            <div
              className="alert alert-warning"
              style={{
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>{googleError}</span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={fetchGoogleStatus}
              >
                Retry
              </button>
            </div>
          )}

          {/* 1. Google Business Profile */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              padding: '16px 0',
              borderBottom: '1px solid #f1f5f9',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 260, flex: 1 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.4rem',
                  flexShrink: 0,
                }}
              >
                <IconGoogleBusiness style={{ color: '#4285F4', width: 24, height: 24 }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                  Google Business Profile
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                  Manage your listings, locations, and local search performance
                </div>
                {isGoogleConnected && googleAccountEmail && (
                  <div style={{ fontSize: '0.74rem', color: '#16a34a', marginTop: 2 }}>
                    Connected as {googleAccountEmail}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              {googleLoading ? (
                <span className="badge badge-gray">Checking...</span>
              ) : isGoogleConnected ? (
                <span className="badge badge-green">Connected</span>
              ) : (
                <span className="badge badge-gray">Not Connected</span>
              )}

              <Link
                to={googleTargetUrl}
                className={`btn btn-sm ${isGoogleConnected ? 'btn-secondary' : 'btn-primary'}`}
              >
                {isGoogleConnected ? 'Manage ' : 'Connect Google '}
              </Link>
            </div>
          </div>

          {/* 2. Google Search Console */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              padding: '16px 0',
              borderBottom: '1px solid #f1f5f9',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 260, flex: 1 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.4rem',
                  flexShrink: 0,
                }}
              >
                <IconGoogleSearch style={{ color: '#34A853', width: 24, height: 24 }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                  Google Search Console
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                  Import organic search queries, clicks, impressions, and indexation data
                </div>
                {isGoogleConnected && googleAccountEmail && (
                  <div style={{ fontSize: '0.74rem', color: '#16a34a', marginTop: 2 }}>
                    Connected as {googleAccountEmail}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              {googleLoading ? (
                <span className="badge badge-gray">Checking...</span>
              ) : isGoogleConnected ? (
                <span className="badge badge-green">Connected</span>
              ) : (
                <span className="badge badge-gray">Not Connected</span>
              )}

              <Link
                to={googleTargetUrl}
                className={`btn btn-sm ${isGoogleConnected ? 'btn-secondary' : 'btn-primary'}`}
              >
                {isGoogleConnected ? 'Manage ' : 'Connect Google '}
              </Link>
            </div>
          </div>

          {/* 3. AI Engine / Provider */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              padding: '16px 0',
              borderBottom: '1px solid #f1f5f9',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 260, flex: 1 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.4rem',
                  flexShrink: 0,
                }}
              >
                <IconGemini style={{ color: '#8b5cf6', width: 24, height: 24 }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                  AI Engine
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                  Powers AI-generated SEO recommendations, opportunity analysis, and content suggestions
                </div>
                <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
                  Provider: Gemini  Configured server-side  Zero credential exposure
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <span className="badge badge-green">Configured</span>
              <Link to={aiTargetUrl} className="btn btn-secondary btn-sm">
                Manage 
              </Link>
            </div>
          </div>

          {/* Connection summary callout */}
          {googleStatus && !googleStatus.connected && !googleLoading && !googleError && (
            <div
              style={{
                marginTop: 20,
                padding: '14px 18px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e3a8a' }}>
                  Google Services Not Connected
                </div>
                <div style={{ fontSize: '0.8rem', color: '#3b82f6', marginTop: 2 }}>
                  Connect your Google account to access Search Console queries and Business Profile metrics.
                </div>
              </div>
              <Link to={googleTargetUrl} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>
                Connect Google
              </Link>
            </div>
          )}

          {googleStatus && googleStatus.connected && (
            <div
              style={{
                marginTop: 20,
                padding: '12px 18px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontSize: '0.82rem', color: '#15803d' }}>
                 Google OAuth 2.0 connection is <strong>active</strong> for your organization.
                {googleAccountEmail && (
                  <span style={{ marginLeft: 6, color: '#166534' }}>
                    ({googleAccountEmail})
                  </span>
                )}
              </div>
              <Link to={googleTargetUrl} className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}>
                Manage Google Integrations 
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Danger zone */}
      <div className="settings-section" style={{ borderColor: '#fecaca' }}>
        <div className="settings-section-header" style={{ borderBottomColor: '#fecaca' }}>
          <div className="settings-section-title" style={{ color: '#dc2626' }}>Danger Zone</div>
          <div className="settings-section-desc">Irreversible actions</div>
        </div>
        <div className="settings-section-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>
                Delete Organization
              </div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                Permanently delete your organization and all associated data.
              </div>
            </div>
            <button className="btn btn-danger btn-sm" disabled title="Not available in Phase 1">
              Delete Organization
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
